// Экран «Схемы» (ТЗ «Графическое планирование», #104 шаг 1 и #105 шаг 2, 2026-09-19):
// чертежи проекта и нарисованные на них зоны-захватки.
// Слева список схем и список зон выбранной схемы, справа чертёж с разметкой.
//
// Почему не картинка: лист паркинга СБ3 — А0 (180 × 91 см) с настоящим текстом.
// Показываем его через pdf.js и ПЕРЕРИСОВЫВАЕМ на каждом масштабе, поэтому текст
// остаётся чётким при любом увеличении. Холст всегда размером с окно просмотра, а
// не со всей страницей: при увеличении в 8 раз страница была бы 40 000 px шириной —
// браузер такой холст не создаст. Рисуется только видимый кусок (`transform`
// в параметрах отрисовки сдвигает страницу под окно).
//
// Зоны лежат на ВТОРОМ холсте поверх чертежа: чертёж рисуется долго и не на каждый
// кадр, а разметка должна успевать за мышью.
//
// Координаты зон — в единицах страницы чертежа, а не в пикселях экрана: зона не
// съезжает ни при каком масштабе и одинакова на любом мониторе. Площадь считает
// сервер по этим же координатам; настоящий масштаб чертежа знать не нужно, потому
// что готовность (шаг 4) — это отношение площадей, и единицы в нём сокращаются.
//
// Сама библиотека pdf.js (1,7 МБ) в общий бандл не попадает: она лежит отдельными
// файлами /web/pdf-web.js и /web/pdf-worker-web.js (кладёт web/pdf_copy.cjs при
// сборке) и подгружается только при заходе на этот экран.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authHdr, call, p, type Data } from '../api';
import { alertBox, confirmBox } from '../dialog';
import type { ShellCtx } from '../shell';

type Shema = Data<'/api/projects/{pid}/shemy', 'get'>[number];
type ZonyOtvet = Data<'/api/shemy/{sid}/zony', 'get'>;
type Zona = ZonyOtvet['zony'][number];
type Primenenie = Data<'/api/shemy/{sid}/primenenie', 'get'>[number];
type TaskShema = Data<'/api/tasks/{tid}/shema', 'get'>;
type Rabota = Data<'/api/projects/{pid}/works', 'get'>[number];
/** минимум, который нужен показу чертежа: и схема из списка, и схема задачи подходят */
type Chertezh = { id: number; nazvanie: string; tip: string; stranica: number };
type Struktura = Data<'/api/projects/{pid}/structure', 'get'>;
/** зона в работе: у новой ещё нет id с сервера, порядок — позиция в списке */
type Risunok = { id: number | null; klyuch: string; nazvanie: string; tochki: [number, number][] };

const MASH_MIN = 0.05, MASH_MAX = 12;
const TIP_IMYA: Record<string, string> = { pdf: 'PDF', png: 'PNG', jpg: 'JPG' };
/** промах по вершине — в пикселях экрана, поэтому переводится в координаты страницы */
const VERSHINA_PX = 7;
type Rezhim = 'ruka' | 'zona' | 'pryamoug';
/** слой: «План» — границы захваток, «Факт» — закрашенное поверх них (#107) */
type Sloj = 'plan' | 'fakt';
type FaktFigura = TaskShema['fakt'][number];

/** «4,3 МБ» — размер файла по-человечески */
function ves(bajt: number) {
  if (!bajt) return '';
  const mb = bajt / 1048576;
  return mb >= 1 ? mb.toFixed(1).replace('.', ',') + ' МБ' : Math.round(bajt / 1024) + ' КБ';
}

/** сегодня в виде ГГГГ-ММ-ДД — дата отметки по умолчанию */
function segodnya() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function kogdaText(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(+d) ? '' : d.toLocaleDateString('ru-RU');
}

/** «1 задача», «2 задачи», «5 задач» — без этого в интерфейсе появляется «1 задач» */
function zadachText(n: number) {
  const sto = n % 100, des = n % 10;
  const slovo = sto >= 11 && sto <= 14 ? 'задач'
    : des === 1 ? 'задача' : des >= 2 && des <= 4 ? 'задачи' : 'задач';
  return n + ' ' + slovo;
}

/** доля зоны в общей площади разметки — понятнее, чем «квадратные пункты страницы» */
function doliText(dolya: number) {
  return dolya >= 0.0995 ? Math.round(dolya * 100) + '%' : (dolya * 100).toFixed(1).replace('.', ',') + '%';
}

/** площадь многоугольника (формула площади Гаусса) — та же, что считает сервер */
function ploshchad(tochki: [number, number][]) {
  let s = 0;
  for (let i = 0; i < tochki.length; i++) {
    const [x1, y1] = tochki[i], [x2, y2] = tochki[(i + 1) % tochki.length];
    s += x1 * y2 - x2 * y1;
  }
  return Math.abs(s) / 2;
}

function vnutri(tochki: [number, number][], x: number, y: number) {
  let da = false;
  for (let i = 0, j = tochki.length - 1; i < tochki.length; j = i++) {
    const [xi, yi] = tochki[i], [xj, yj] = tochki[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) da = !da;
  }
  return da;
}

function centr(tochki: [number, number][]): [number, number] {
  const x = tochki.reduce((s, t) => s + t[0], 0) / tochki.length;
  const y = tochki.reduce((s, t) => s + t[1], 0) / tochki.length;
  return [x, y];
}

/** «З1», «З2» … — первое свободное имя */
function svobodnoeImya(est: Risunok[]) {
  const zanyato = new Set(est.map(z => z.nazvanie.toLowerCase()));
  for (let i = 1; i < 999; i++) {
    const imya = 'З' + i;
    if (!zanyato.has(imya.toLowerCase())) return imya;
  }
  return 'Зона';
}

let nomerKlyucha = 0;
const novyjKlyuch = () => 'z' + (++nomerKlyucha);
const izZony = (z: Zona): Risunok => ({
  id: z.id, klyuch: novyjKlyuch(), nazvanie: z.nazvanie,
  tochki: (z.geom.tochki as number[][]).map(t => [t[0], t[1]] as [number, number]),
});

/* ---------- pdf.js: грузится один раз на весь сеанс, при первом открытии PDF ---------- */
type PdfZadacha = { promise: Promise<PdfDoc>; destroy: () => Promise<void> };
type PdfLib = { getDocument: (o: unknown) => PdfZadacha;
                GlobalWorkerOptions: { workerSrc: string } };
// у документа метода destroy в pdf.js 6 нет — закрывается ЗАДАЧА загрузки
// (проверено на тесте 2026-09-19: doc.destroy роняло экран при перечитывании)
type PdfDoc = { numPages: number; getPage: (n: number) => Promise<PdfPage> };
type PdfViewport = { width: number; height: number };
type PdfPage = { getViewport: (o: { scale: number }) => PdfViewport;
                 render: (o: unknown) => { promise: Promise<void>; cancel: () => void } };

let pdfLibPromise: Promise<PdfLib> | null = null;
// адрес в переменной, а не строкой в import(): сборщик не должен искать этот файл
// у себя — он кладётся рядом с бандлом отдельно (web/pdf_copy.cjs)
const PDF_ADRES = '/web/pdf-web.js';
function pdfLib(): Promise<PdfLib> {
  if (!pdfLibPromise) {
    pdfLibPromise = import(/* @vite-ignore */ PDF_ADRES).then((m: unknown) => {
      const lib = m as PdfLib;
      lib.GlobalWorkerOptions.workerSrc = '/web/pdf-worker-web.js';
      return lib;
    });
  }
  return pdfLibPromise;
}

/* ---------- просмотр чертежа с разметкой ---------- */

type Gruz =
  | { vid: 'pdf'; zadacha: PdfZadacha; page: PdfPage; stranic: number; w: number; h: number }
  | { vid: 'img'; img: HTMLImageElement; w: number; h: number };

function Prosmotr({ shema, zony, vybrana, rezhim, mozhno, sloj, fakt, onOshibka, onZony,
                   onVybrat, onFakt, onSteret }: {
  shema: Chertezh; zony: Risunok[]; vybrana: string | null; rezhim: Rezhim; mozhno: boolean;
  sloj: Sloj; fakt: FaktFigura[];
  onOshibka: (m: string) => void;
  onZony: (f: (z: Risunok[]) => Risunok[]) => void;
  onVybrat: (k: string | null) => void;
  /** закрасить: новая фигура факта в зоне с сервера (zona_id) */
  onFakt: (zonaId: number, tochki: [number, number][]) => void;
  /** стереть фигуру факта по её номеру */
  onSteret: (id: number) => void;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const holst = useRef<HTMLCanvasElement | null>(null);       // чертёж
  const holstRazmetki = useRef<HTMLCanvasElement | null>(null);   // холст разметки поверх чертежа
  const gruz = useRef<Gruz | null>(null);
  const zadacha = useRef<{ cancel: () => void } | null>(null);
  const kadr = useRef(0);
  const kadrSloya = useRef(0);
  // масштаб и левый верхний угол окна просмотра в координатах увеличенной страницы
  const vid = useRef({ mash: 1, x: 0, y: 0 });
  // зона, которую рисуют прямо сейчас: держим в ref, чтобы не дёргать React на каждый пиксель
  const cherno = useRef<{ tochki: [number, number][]; kursor: [number, number] | null } | null>(null);
  const svezhie = useRef({ zony, vybrana, mozhno, sloj, fakt });
  svezhie.current = { zony, vybrana, mozhno, sloj, fakt };
  // ссылку на «сообщить об ошибке» держим в ref: она пересоздаётся на каждой
  // перерисовке родителя, а от неё зависит загрузка чертежа — иначе лист А0
  // перечитывался бы с сервера по любому чиху
  const soobshchit = useRef(onOshibka);
  soobshchit.current = onOshibka;

  const [mashPokaz, setMashPokaz] = useState(1);
  const [stranica, setStranica] = useState(shema.stranica || 1);
  const [stranic, setStranic] = useState(1);
  const [gruzitsya, setGruzitsya] = useState(true);
  const [gotovo, setGotovo] = useState(false);

  /* --- перевод координат: страница ↔ экран --- */
  const naEkran = (px: number, py: number): [number, number] => {
    const { mash, x, y } = vid.current;
    return [px * mash - x, py * mash - y];
  };
  const naStranicu = (cx: number, cy: number): [number, number] => {
    const { mash, x, y } = vid.current;
    return [(cx + x) / mash, (cy + y) / mash];
  };
  const tochkaMyshi = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = box.current!.getBoundingClientRect();
    return naStranicu(e.clientX - r.left, e.clientY - r.top);
  };

  /* --- разметка: рисуется синхронно на своём холсте --- */
  const risovatSloj = useCallback(() => {
    const c = holstRazmetki.current, el = box.current;
    if (!c || !el) return;
    const dpr = window.devicePixelRatio || 1;
    const W = el.clientWidth, H = el.clientHeight;
    if (W <= 0 || H <= 0) return;
    if (c.width !== Math.round(W * dpr) || c.height !== Math.round(H * dpr)) {
      c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
      c.style.width = W + 'px'; c.style.height = H + 'px';
    }
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const { zony: zz, vybrana: vyb, sloj: sl, fakt: ff } = svezhie.current;
    ctx.lineJoin = 'round';
    // закрашенное рисуется ПОД границами зон, чтобы границы оставались видны
    if (sl === 'fakt') {
      ff.forEach(f => {
        const tt = (f.geom.tochki as number[][]) || [];
        if (tt.length < 3) return;
        ctx.beginPath();
        tt.forEach((t, j) => {
          const [x, y] = naEkran(t[0], t[1]);
          if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        ctx.closePath();
        ctx.fillStyle = 'rgba(22,163,74,.38)';
        ctx.fill();
        ctx.strokeStyle = '#16a34a'; ctx.lineWidth = 1;
        ctx.stroke();
      });
    }
    zz.forEach((z, i) => {
      if (z.tochki.length < 2) return;
      const vybor = z.klyuch === vyb;
      ctx.beginPath();
      z.tochki.forEach((t, j) => {
        const [x, y] = naEkran(t[0], t[1]);
        if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.closePath();
      ctx.fillStyle = vybor ? 'rgba(59,130,246,.22)' : 'rgba(59,130,246,.10)';
      ctx.fill();
      ctx.strokeStyle = vybor ? '#2563eb' : '#60a5fa';
      ctx.lineWidth = vybor ? 2.5 : 1.5;
      ctx.stroke();
      // подпись в середине зоны
      const [cx, cy] = naEkran(...centr(z.tochki));
      ctx.font = '600 12px system-ui, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const podpis = (i + 1) + '. ' + z.nazvanie;
      const w = ctx.measureText(podpis).width;
      ctx.fillStyle = 'rgba(255,255,255,.85)';
      ctx.fillRect(cx - w / 2 - 4, cy - 9, w + 8, 18);
      ctx.fillStyle = '#1d4ed8';
      ctx.fillText(podpis, cx, cy);
      // вершины выбранной зоны — их можно тянуть
      if (vybor && svezhie.current.mozhno) {
        ctx.fillStyle = '#fff'; ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 1.5;
        z.tochki.forEach(t => {
          const [x, y] = naEkran(t[0], t[1]);
          ctx.beginPath(); ctx.arc(x, y, 4.5, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        });
      }
    });
    // зона, которую сейчас рисуют
    const ch = cherno.current;
    if (ch && ch.tochki.length) {
      ctx.beginPath();
      ch.tochki.forEach((t, j) => {
        const [x, y] = naEkran(t[0], t[1]);
        if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      if (ch.kursor) {
        const [x, y] = naEkran(ch.kursor[0], ch.kursor[1]);
        ctx.lineTo(x, y);
      }
      ctx.strokeStyle = '#dc2626'; ctx.lineWidth = 2; ctx.setLineDash([5, 4]);
      ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = '#dc2626';
      ch.tochki.forEach(t => {
        const [x, y] = naEkran(t[0], t[1]);
        ctx.beginPath(); ctx.arc(x, y, 3.5, 0, Math.PI * 2); ctx.fill();
      });
    }
  }, []);

  const kadrSloyaZapros = useCallback(() => {
    if (kadrSloya.current) return;
    kadrSloya.current = requestAnimationFrame(() => { kadrSloya.current = 0; risovatSloj(); });
  }, [risovatSloj]);

  /** перерисовать видимый кусок чертежа; на каждом масштабе заново — текст чёткий */
  const risovat = useCallback(() => {
    const c = holst.current, el = box.current, g = gruz.current;
    if (!c || !el || !g) return;
    const dpr = window.devicePixelRatio || 1;
    const W = el.clientWidth, H = el.clientHeight;
    if (W <= 0 || H <= 0) return;
    if (c.width !== Math.round(W * dpr) || c.height !== Math.round(H * dpr)) {
      c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
      c.style.width = W + 'px'; c.style.height = H + 'px';
    }
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const { mash, x, y } = vid.current;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    if (zadacha.current) { zadacha.current.cancel(); zadacha.current = null; }
    if (g.vid === 'img') {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(g.img, -x / mash, -y / mash, W / mash, H / mash, 0, 0, W, H);
      return;
    }
    const viewport = g.page.getViewport({ scale: mash });
    const t = g.page.render({
      canvasContext: ctx, viewport, canvas: c,
      // сдвигаем страницу так, чтобы в холст попал только видимый кусок
      transform: [dpr, 0, 0, dpr, -x * dpr, -y * dpr],
    });
    zadacha.current = t;
    t.promise.then(() => { zadacha.current = null; },
      () => { /* отменили при следующем кадре — это нормально */ });
  }, []);

  const zaprositKadr = useCallback(() => {
    kadrSloyaZapros();
    if (kadr.current) return;
    kadr.current = requestAnimationFrame(() => { kadr.current = 0; risovat(); });
  }, [risovat, kadrSloyaZapros]);

  /** вписать страницу в окно по ширине */
  const vpisat = useCallback(() => {
    const el = box.current, g = gruz.current;
    if (!el || !g) return;
    const mash = Math.max(MASH_MIN, Math.min(MASH_MAX, (el.clientWidth - 24) / g.w));
    vid.current = { mash, x: -12, y: -12 };
    setMashPokaz(mash);
    zaprositKadr();
  }, [zaprositKadr]);

  // зоны поменялись снаружи (список, отмена, выбор) — перерисовать слой
  useEffect(() => { kadrSloyaZapros(); }, [zony, vybrana, rezhim, kadrSloyaZapros]);

  // загрузка чертежа: файл берём с заголовком входа — прямой ссылки у чертежей нет
  useEffect(() => {
    let zhiv = true;
    setGotovo(false); setGruzitsya(true);
    (async () => {
      try {
        const otvet = await fetch(p('/api/shemy/{sid}/fajl', { sid: shema.id }), { headers: authHdr() });
        if (!otvet.ok) throw new Error('чертёж не отдался (' + otvet.status + ')');
        const dannye = await otvet.arrayBuffer();
        if (!zhiv) return;
        if (shema.tip === 'pdf') {
          const lib = await pdfLib();
          const zadachaPdf = lib.getDocument({ data: dannye });
          const doc = await zadachaPdf.promise;
          if (!zhiv) { void zadachaPdf.destroy(); return; }
          const nomer = Math.min(Math.max(1, stranica), doc.numPages);
          const page = await doc.getPage(nomer);
          const vp = page.getViewport({ scale: 1 });
          if (!zhiv) { void zadachaPdf.destroy(); return; }
          gruz.current = { vid: 'pdf', zadacha: zadachaPdf, page,
                           stranic: doc.numPages, w: vp.width, h: vp.height };
          setStranic(doc.numPages);
        } else {
          const img = new Image();
          img.src = URL.createObjectURL(new Blob([dannye]));
          await img.decode();
          if (!zhiv) return;
          gruz.current = { vid: 'img', img, w: img.naturalWidth, h: img.naturalHeight };
          setStranic(1);
        }
        setGotovo(true); setGruzitsya(false);
        vpisat();
      } catch (e) {
        if (!zhiv) return;
        setGruzitsya(false);
        soobshchit.current('Не удалось открыть чертёж: ' + (e instanceof Error ? e.message : String(e)));
      }
    })();
    return () => {
      zhiv = false;
      if (zadacha.current) { zadacha.current.cancel(); zadacha.current = null; }
      const g = gruz.current;
      if (g && g.vid === 'pdf') void g.zadacha.destroy();
      if (g && g.vid === 'img') URL.revokeObjectURL(g.img.src);
      gruz.current = null;
    };
  }, [shema.id, shema.tip, stranica, vpisat]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => zaprositKadr());
    ro.observe(el);
    return () => ro.disconnect();
  }, [zaprositKadr]);

  /** увеличение с привязкой к точке под курсором */
  const menyatMash = useCallback((k: number, cx?: number, cy?: number) => {
    const el = box.current;
    if (!el) return;
    const { mash, x, y } = vid.current;
    const novyj = Math.max(MASH_MIN, Math.min(MASH_MAX, mash * k));
    if (novyj === mash) return;
    const px = cx ?? el.clientWidth / 2, py = cy ?? el.clientHeight / 2;
    vid.current = { mash: novyj, x: (x + px) * (novyj / mash) - px, y: (y + py) * (novyj / mash) - py };
    setMashPokaz(novyj);
    zaprositKadr();
  }, [zaprositKadr]);

  function koleso(e: React.WheelEvent) {
    const el = box.current;
    if (!el) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    if (e.ctrlKey || e.metaKey) {
      menyatMash(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX - r.left, e.clientY - r.top);
    } else {
      vid.current.x += e.deltaX; vid.current.y += e.deltaY;
      zaprositKadr();
    }
  }

  /** тянем чертёж (правой кнопкой — в любом режиме, левой — в режиме «Рука» по пустому месту) */
  function tyanutChertezh(e: { clientX: number; clientY: number }) {
    const el = box.current;
    if (!el) return;
    const x0 = e.clientX, y0 = e.clientY, sx = vid.current.x, sy = vid.current.y;
    el.style.cursor = 'grabbing';
    const dvizh = (ev: MouseEvent) => {
      vid.current.x = sx - (ev.clientX - x0);
      vid.current.y = sy - (ev.clientY - y0);
      zaprositKadr();
    };
    const konec = () => {
      el.style.cursor = '';
      window.removeEventListener('mousemove', dvizh);
      window.removeEventListener('mouseup', konec);
    };
    window.addEventListener('mousemove', dvizh);
    window.addEventListener('mouseup', konec);
  }

  /** закончить многоугольник: три точки и больше — зона, меньше — бросаем */
  const zakonchitZonu = useCallback(() => {
    const ch = cherno.current;
    cherno.current = null;
    if (ch && ch.tochki.length >= 3) {
      const klyuch = novyjKlyuch();
      onZony(z => [...z, { id: null, klyuch, nazvanie: svobodnoeImya(z), tochki: ch.tochki }]);
      onVybrat(klyuch);
    }
    kadrSloyaZapros();
  }, [onZony, onVybrat, kadrSloyaZapros]);

  // Enter — закончить зону, Esc — бросить начатую
  useEffect(() => {
    const knopka = (e: KeyboardEvent) => {
      if (!cherno.current) return;
      if (e.key === 'Enter') { e.preventDefault(); zakonchitZonu(); }
      if (e.key === 'Escape') { e.preventDefault(); cherno.current = null; kadrSloyaZapros(); }
    };
    window.addEventListener('keydown', knopka);
    return () => window.removeEventListener('keydown', knopka);
  }, [zakonchitZonu, kadrSloyaZapros]);

  function myshVniz(e: React.MouseEvent) {
    if (e.button === 2 || e.button === 1) { tyanutChertezh(e); return; }
    if (e.button !== 0) return;
    // Alt + клик по закрашенному — стереть эту фигуру (#107)
    if (sloj === 'fakt' && mozhno && e.altKey && box.current) {
      const [ax, ay] = tochkaMyshi(e);
      const pod = [...fakt].reverse().find(f => {
        const tt = ((f.geom.tochki as number[][]) || []).map(t => [t[0], t[1]] as [number, number]);
        return tt.length >= 3 && vnutri(tt, ax, ay);
      });
      if (pod) { onSteret(pod.id); return; }
    }
    if (!box.current) return;
    const [px, py] = tochkaMyshi(e);

    /* ---- слой «Факт»: закрасить зону целиком, обвести часть, стереть ---- */
    if (sloj === 'fakt' && mozhno) {
      const podZonoj = [...zony].reverse().find(z => z.tochki.length >= 3 && vnutri(z.tochki, px, py));
      if (rezhim === 'ruka') {            // «Закрасить»: клик по зоне — она готова целиком
        if (podZonoj && podZonoj.id != null) { onVybrat(podZonoj.klyuch); onFakt(podZonoj.id, podZonoj.tochki); }
        else if (!podZonoj) tyanutChertezh(e);
        return;
      }
      if (rezhim === 'pryamoug' || rezhim === 'zona') {
        if (!podZonoj || podZonoj.id == null) {
          onOshibka('Начните внутри зоны — закрашенное живёт в своей захватке');
          return;
        }
        const zid = podZonoj.id;
        if (rezhim === 'pryamoug') {
          const dvizh = (ev: MouseEvent) => {
            const [x, y] = tochkaMyshi(ev);
            cherno.current = { tochki: [[px, py], [x, py], [x, y], [px, y]], kursor: null };
            kadrSloyaZapros();
          };
          const konec = () => {
            window.removeEventListener('mousemove', dvizh);
            window.removeEventListener('mouseup', konec);
            const ch = cherno.current;
            cherno.current = null;
            if (ch && ploshchad(ch.tochki) > 1) onFakt(zid, ch.tochki);
            kadrSloyaZapros();
          };
          window.addEventListener('mousemove', dvizh);
          window.addEventListener('mouseup', konec);
          return;
        }
        if (e.detail === 2) {                       // конец многоугольника
          const ch = cherno.current;
          cherno.current = null;
          if (ch && ch.tochki.length >= 3) onFakt(zid, ch.tochki);
          kadrSloyaZapros();
          return;
        }
        const ch = cherno.current || { tochki: [], kursor: null };
        ch.tochki = [...ch.tochki, [px, py]];
        cherno.current = ch;
        kadrSloyaZapros();
        return;
      }
    }
    if (sloj === 'fakt') { tyanutChertezh(e); return; }

    // многоугольник: клик — угол, двойной клик — конец
    if (rezhim === 'zona' && mozhno) {
      if (e.detail === 2) { zakonchitZonu(); return; }
      const ch = cherno.current || { tochki: [], kursor: null };
      ch.tochki = [...ch.tochki, [px, py]];
      cherno.current = ch;
      kadrSloyaZapros();
      return;
    }

    // прямоугольник: тянем от угла до угла
    if (rezhim === 'pryamoug' && mozhno) {
      const dvizh = (ev: MouseEvent) => {
        const [x, y] = tochkaMyshi(ev);
        cherno.current = { tochki: [[px, py], [x, py], [x, y], [px, y]], kursor: null };
        kadrSloyaZapros();
      };
      const konec = () => {
        window.removeEventListener('mousemove', dvizh);
        window.removeEventListener('mouseup', konec);
        const ch = cherno.current;
        cherno.current = null;
        // случайный клик без протяжки зоной не считаем
        if (ch && ploshchad(ch.tochki) > 1) {
          const klyuch = novyjKlyuch();
          onZony(z => [...z, { id: null, klyuch, nazvanie: svobodnoeImya(z), tochki: ch.tochki }]);
          onVybrat(klyuch);
        }
        kadrSloyaZapros();
      };
      window.addEventListener('mousemove', dvizh);
      window.addEventListener('mouseup', konec);
      return;
    }

    // режим «Рука»: вершина выбранной зоны → тянем вершину; внутри зоны → двигаем зону
    const dopusk = VERSHINA_PX / vid.current.mash;
    const vyb = zony.find(z => z.klyuch === vybrana);
    if (mozhno && vyb) {
      const i = vyb.tochki.findIndex(t => Math.abs(t[0] - px) <= dopusk && Math.abs(t[1] - py) <= dopusk);
      if (i >= 0) {
        const dvizh = (ev: MouseEvent) => {
          const [x, y] = tochkaMyshi(ev);
          onZony(zz => zz.map(z => (z.klyuch !== vyb.klyuch ? z
            : { ...z, tochki: z.tochki.map((t, j) => (j === i ? [x, y] as [number, number] : t)) })));
        };
        const konec = () => {
          window.removeEventListener('mousemove', dvizh);
          window.removeEventListener('mouseup', konec);
        };
        window.addEventListener('mousemove', dvizh);
        window.addEventListener('mouseup', konec);
        return;
      }
    }
    // сверху — та зона, что нарисована позже
    const pod = [...zony].reverse().find(z => z.tochki.length >= 3 && vnutri(z.tochki, px, py));
    onVybrat(pod ? pod.klyuch : null);
    if (pod && mozhno) {
      const ishodnye = pod.tochki;
      const dvizh = (ev: MouseEvent) => {
        const [x, y] = tochkaMyshi(ev);
        const dx = x - px, dy = y - py;
        if (Math.abs(dx) * vid.current.mash < 3 && Math.abs(dy) * vid.current.mash < 3) return;
        onZony(zz => zz.map(z => (z.klyuch !== pod.klyuch ? z
          : { ...z, tochki: ishodnye.map(t => [t[0] + dx, t[1] + dy] as [number, number]) })));
      };
      const konec = () => {
        window.removeEventListener('mousemove', dvizh);
        window.removeEventListener('mouseup', konec);
      };
      window.addEventListener('mousemove', dvizh);
      window.addEventListener('mouseup', konec);
      return;
    }
    if (!pod && rezhim === 'ruka') tyanutChertezh(e);
  }

  function myshDvizh(e: React.MouseEvent) {
    if (!cherno.current || rezhim !== 'zona') return;
    cherno.current.kursor = tochkaMyshi(e);
    kadrSloyaZapros();
  }

  return (
    <div className="sh-prosmotr">
      <div className="sh-panel">
        <b title={shema.nazvanie}>{shema.nazvanie}</b>
        <span className="sh-tip">{TIP_IMYA[shema.tip] || shema.tip}</span>
        {stranic > 1 && (
          <span className="sh-str">
            Стр.
            <button className="sec" disabled={stranica <= 1}
              onClick={() => setStranica(n => Math.max(1, n - 1))}>‹</button>
            {stranica} из {stranic}
            <button className="sec" disabled={stranica >= stranic}
              onClick={() => setStranica(n => Math.min(stranic, n + 1))}>›</button>
          </span>
        )}
        <span className="sh-rast" />
        <button className="sec" title="Мельче" onClick={() => menyatMash(1 / 1.3)}>−</button>
        <span className="sh-mash" title="Масштаб показа">{Math.round(mashPokaz * 100)}%</span>
        <button className="sec" title="Крупнее" onClick={() => menyatMash(1.3)}>+</button>
        <button className="sec" title="Вписать чертёж в окно" onClick={vpisat}>По ширине</button>
      </div>
      <div className="sh-holst" ref={box}
        style={{ cursor: sloj === 'fakt' ? (rezhim === 'ruka' ? 'pointer' : 'crosshair')
          : (rezhim === 'ruka' ? 'grab' : 'crosshair') }}
        onWheel={koleso} onMouseDown={myshVniz} onMouseMove={myshDvizh}
        onContextMenu={e => e.preventDefault()}>
        <canvas ref={holst} />
        <canvas ref={holstRazmetki} className="sh-sloj" />
        {gruzitsya && <div className="sh-zhdi">Открываю чертёж…</div>}
        {gotovo && (
          <div className="sh-podskazka">
            {sloj === 'fakt'
              ? (rezhim === 'ruka' ? 'Клик по зоне — она сделана целиком; Alt + клик по закрашенному — стереть'
                : 'Обведите сделанную часть ВНУТРИ зоны; Alt + клик по закрашенному — стереть')
              : rezhim === 'zona' ? 'Клики — углы зоны, двойной клик или Enter — закончить, Esc — бросить'
                : rezhim === 'pryamoug' ? 'Тянуть мышью — прямоугольная зона'
                  : 'Тянуть мышью — двигать чертёж, клик по зоне — выбрать, Ctrl + колесо — увеличить'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- схема задачи для карточки задачи (#109) ---------- */

/** Чертёж с захватками и закрашенным по одной задаче, только смотреть.
    Правится всё на экране «Схемы» — здесь планер видит картинку рядом с задачей. */
export function ShemaZadachi({ tid }: { tid: number }) {
  const [d, setD] = useState<TaskShema | null>(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    let zhiv = true;
    void (async () => {
      const r = await call<TaskShema>('get', p('/api/tasks/{tid}/shema', { tid }));
      if (!zhiv) return;
      if (!r.ok) { setMsg(r.error.message); return; }
      setD(r.data);
    })();
    return () => { zhiv = false; };
  }, [tid]);

  if (msg) return <div className="err">{msg}</div>;
  if (!d) return <div className="sh-pusto">Загружаю…</div>;
  if (!d.shema) {
    return (
      <div className="sh-pusto">
        К этой задаче схема захваток не применена. Схемы заводятся в разделе
        «Справочники → Схемы», там же они применяются к работам и этажам.
      </div>
    );
  }
  const vsya = d.zony.reduce((s2, z) => s2 + z.ploshchad, 0);
  const sdelano = d.zony.reduce((s2, z) => s2 + z.sdelano, 0);
  const risunki: Risunok[] = d.zony.map(z => ({
    id: z.id, klyuch: 'k' + z.id, nazvanie: z.nazvanie, tochki: [],
  }));
  return (
    <div className="sh-kartochka">
      <div className="sh-kartochka-verh">
        <b>{d.shema.nazvanie}</b>
        <span className="sh-meta">{d.rabota} · {d.korpus} · этаж {d.etazh}</span>
        <span className="sh-rast" />
        <span>Готовность по площади: <b>{vsya > 0 ? Math.round(100 * sdelano / vsya) : 0}%</b></span>
      </div>
      <div className="sh-kartochka-telo">
        <ShemaTolkoSmotret shema={d.shema} zony={risunki} fakt={d.fakt} />
        <div className="sh-kartochka-spisok">
          {d.zony.map((z, i) => (
            <div key={z.id} className="sh-zona">
              <span className="sh-nomer">{i + 1}</span>
              <span className="sh-zona-imya" title={z.nazvanie}>{z.nazvanie}</span>
              <span className="sh-dolya sh-gotovo">{z.procent}%</span>
            </div>
          ))}
          <div className="sh-meta">Разметка и отметки правятся на экране «Схемы».</div>
        </div>
      </div>
    </div>
  );
}

/** Чертёж с зонами: границы берём из самой схемы, потому что задача их не присылает */
function ShemaTolkoSmotret({ shema, zony, fakt }: {
  shema: Chertezh; zony: Risunok[]; fakt: FaktFigura[];
}) {
  const [granicy, setGranicy] = useState<Risunok[]>([]);
  useEffect(() => {
    let zhiv = true;
    void (async () => {
      const r = await call<ZonyOtvet>('get', p('/api/shemy/{sid}/zony', { sid: shema.id }));
      if (zhiv && r.ok) setGranicy(r.data.zony.map(izZony));
    })();
    return () => { zhiv = false; };
  }, [shema.id]);
  const gotovye = granicy.length ? granicy : zony;
  return (
    <Prosmotr key={shema.id} shema={shema} zony={gotovye} vybrana={null} rezhim="ruka"
      mozhno={false} sloj="fakt" fakt={fakt} onOshibka={() => { /* показывать негде */ }}
      onZony={() => { /* только смотрим */ }} onVybrat={() => { /* только смотрим */ }}
      onFakt={() => { /* только смотрим */ }} onSteret={() => { /* только смотрим */ }} />
  );
}

/* ---------- окно «Новая схема» ---------- */

function NovayaShema({ pid, onGotovo, onOtmena, onOshibka }: {
  pid: number; onGotovo: () => void; onOtmena: () => void; onOshibka: (m: string) => void;
}) {
  const [nazvanie, setNazvanie] = useState('');
  const [fajl, setFajl] = useState<File | null>(null);
  const [idet, setIdet] = useState(false);

  async function sohranit() {
    if (!nazvanie.trim()) { onOshibka('Дайте схеме название'); return; }
    if (!fajl) { onOshibka('Выберите файл чертежа'); return; }
    setIdet(true);
    const telo = new FormData();
    telo.append('nazvanie', nazvanie.trim());
    telo.append('fajl', fajl);
    try {
      const otvet = await fetch(p('/api/projects/{pid}/shemy', { pid }),
        { method: 'POST', headers: authHdr(), body: telo });
      const r = await otvet.json().catch(() => null);
      if (!otvet.ok || !r || !r.ok) {
        // 413 от nginx приходит страницей, а не нашим JSON — переводим по-человечески
        onOshibka(r?.error?.message
          || (otvet.status === 413 ? 'Файл слишком большой — предел 25 МБ' : 'Не удалось загрузить чертёж'));
        setIdet(false);
        return;
      }
      onGotovo();
    } catch (e) {
      onOshibka('Не удалось загрузить чертёж: ' + (e instanceof Error ? e.message : String(e)));
      setIdet(false);
    }
  }

  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) onOtmena(); }}>
      <div className="dlg" role="dialog" aria-modal="true">
        <div className="dlg-text">Новая схема</div>
        <div className="dlg-podpis">
          Чертёж, на котором размечаются захватки. Подходят PDF, PNG и JPG до 25 МБ.
          Файлы AutoCAD (.dwg) пока не открываем — сохраните лист в PDF, так текст
          останется чётким при увеличении.
        </div>
        <input className="dlg-input" placeholder="Название, например «Паркинг, фундаментная плита»"
          value={nazvanie} autoFocus onChange={e => setNazvanie(e.target.value)} />
        <input className="dlg-input" type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
          onChange={e => setFajl(e.target.files?.[0] || null)} />
        {fajl && <div className="dlg-podpis">{fajl.name} · {ves(fajl.size)}</div>}
        <div className="dlg-knopki">
          <button className="sec" onClick={onOtmena} disabled={idet}>Отмена</button>
          <button onClick={() => void sohranit()} disabled={idet}>{idet ? 'Загружаю…' : 'Загрузить'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- окно «Применить схему к работам и этажам» ---------- */

function PrimenitOkno({ pid, sid, zony, onGotovo, onOtmena, onOshibka }: {
  pid: number; sid: number; zony: Risunok[];
  onGotovo: (zadach: number) => void; onOtmena: () => void; onOshibka: (m: string) => void;
}) {
  const [raboty, setRaboty] = useState<Rabota[]>([]);
  const [struktura, setStruktura] = useState<Struktura>({} as Struktura);
  const [poisk, setPoisk] = useState('');
  const [vybranyRaboty, setVybranyRaboty] = useState<number[]>([]);
  const [korpus, setKorpus] = useState('');
  const [vybranyEtazhi, setVybranyEtazhi] = useState<number[]>([]);
  const [obemy, setObemy] = useState<Record<number, string>>({});
  const [ed, setEd] = useState('м³');
  const [idet, setIdet] = useState(false);
  // отказ показываем прямо в окне: строка ошибки экрана остаётся за ним и не видна
  const [oshibka, setOshibka] = useState('');
  const ne = (m: string) => { setOshibka(m); onOshibka(m); };

  useEffect(() => {
    void (async () => {
      const [w, st] = await Promise.all([
        call<Rabota[]>('get', p('/api/projects/{pid}/works', { pid })),
        call<Struktura>('get', p('/api/projects/{pid}/structure', { pid })),
      ]);
      if (w.ok) setRaboty(w.data);
      if (st.ok) {
        setStruktura(st.data);
        const pervyj = Object.keys(st.data)[0] || '';
        setKorpus(pervyj);
      }
    })();
  }, [pid]);

  const etazhi = (struktura as Record<string, { id: number; etazh: string; tip: string | null }[]>)[korpus] || [];
  const vidnyRaboty = raboty.filter(w => !poisk
    || w.name.toLowerCase().includes(poisk.toLowerCase().trim()));

  function perekl(spisok: number[], id: number) {
    return spisok.includes(id) ? spisok.filter(x => x !== id) : [...spisok, id];
  }

  async function primenit() {
    if (!vybranyRaboty.length) { ne('Выберите хотя бы одну работу'); return; }
    if (!vybranyEtazhi.length) { ne('Выберите хотя бы один этаж'); return; }
    setOshibka(''); setIdet(true);
    const chisla: Record<string, number | null> = {};
    for (const z of zony) {
      if (z.id == null) continue;
      const v = (obemy[z.id] || '').trim();
      chisla[String(z.id)] = v ? Number(v.replace(',', '.')) : null;
    }
    const r = await call<{ zadach: number; zon: number }>('post',
      p('/api/shemy/{sid}/primenit', { sid }),
      { work_ids: vybranyRaboty, floor_ids: vybranyEtazhi, obemy: chisla, ed_izm: ed.trim() || null });
    setIdet(false);
    if (!r.ok) { ne(r.error.message); return; }
    onGotovo(r.data.zadach);
  }

  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) onOtmena(); }}>
      <div className="dlg sh-pr-okno" role="dialog" aria-modal="true">
        <div className="dlg-text">Применить схему к работам и этажам</div>
        <div className="dlg-podpis">
          Границы захваток у этажа одни, а объёмы и сроки у каждой работы свои. Объём
          вводится один раз и размножается на все выбранные этажи — потом его можно
          поправить у отдельной задачи.
        </div>
        <div className="sh-pr-telo">
          <div className="sh-pr-stolb">
            <b>Работы</b>
            <input placeholder="Найти работу…" value={poisk} onChange={e => setPoisk(e.target.value)} />
            <div className="sh-pr-spisok">
              {vidnyRaboty.map(w => (
                <label key={w.id}>
                  <input type="checkbox" checked={vybranyRaboty.includes(w.id)}
                    onChange={() => setVybranyRaboty(v => perekl(v, w.id))} />
                  {w.name}
                </label>
              ))}
              {!vidnyRaboty.length && <div className="sh-pusto">Работы не найдены</div>}
            </div>
          </div>
          <div className="sh-pr-stolb">
            <b>Этажи</b>
            <select value={korpus} onChange={e => { setKorpus(e.target.value); setVybranyEtazhi([]); }}>
              {Object.keys(struktura).map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <div className="sh-pr-knopki">
              <a onClick={() => setVybranyEtazhi(etazhi.map(f => f.id))}>все</a>
              <a onClick={() => setVybranyEtazhi(etazhi.filter(f => f.tip === 'Типовой').map(f => f.id))}>типовые</a>
              <a onClick={() => setVybranyEtazhi([])}>снять</a>
            </div>
            <div className="sh-pr-spisok">
              {etazhi.map(f => (
                <label key={f.id}>
                  <input type="checkbox" checked={vybranyEtazhi.includes(f.id)}
                    onChange={() => setVybranyEtazhi(v => perekl(v, f.id))} />
                  {f.etazh}<span className="sh-meta"> · {f.tip || '—'}</span>
                </label>
              ))}
              {!etazhi.length && <div className="sh-pusto">В корпусе нет этажей</div>}
            </div>
          </div>
          <div className="sh-pr-stolb">
            <b>Объёмы зон</b>
            <div className="sh-pr-ed">
              Единица: <input value={ed} onChange={e => setEd(e.target.value)} style={{ width: 70 }} />
            </div>
            <div className="sh-pr-spisok">
              {zony.map((z, i) => (
                <div key={z.klyuch} className="sh-pr-obem">
                  <span>{i + 1}. {z.nazvanie}</span>
                  <input value={z.id != null ? (obemy[z.id] || '') : ''} placeholder="—"
                    disabled={z.id == null}
                    onChange={e => z.id != null && setObemy(o => ({ ...o, [z.id as number]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="dlg-podpis">Объём — справочная цифра. Готовность считается по площади.</div>
          </div>
        </div>
        {oshibka && <div className="sh-oshibka">{oshibka}</div>}
        <div className="dlg-knopki">
          <button className="sec" onClick={onOtmena} disabled={idet}>Отмена</button>
          <button onClick={() => void primenit()} disabled={idet}>
            {idet ? 'Применяю…' : 'Применить'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- экран ---------- */

export function Shemy({ ctx }: { ctx: ShellCtx }) {
  const [rows, setRows] = useState<Shema[]>([]);
  const [msg, setMsg] = useState('');
  const [vybranaShema, setVybranaShema] = useState<number | null>(null);
  const [novaya, setNovaya] = useState(false);
  const [zony, setZony] = useState<Risunok[]>([]);
  const [bylo, setBylo] = useState<Risunok[]>([]);       // как на сервере: отсюда «не сохранено»
  const [rev, setRev] = useState(0);
  const [vybranaZona, setVybranaZona] = useState<string | null>(null);
  const [rezhim, setRezhim] = useState<Rezhim>('ruka');
  const [pishem, setPishem] = useState(false);
  // куда схема применена и объёмы выбранной задачи (шаг 3, #106)
  const [gde, setGde] = useState<Primenenie[]>([]);
  const [zadacha, setZadacha] = useState<TaskShema | null>(null);
  const [tid, setTid] = useState<number | null>(null);
  const [primenyaem, setPrimenyaem] = useState(false);
  const [sloj, setSloj] = useState<Sloj>('plan');
  const [dataOtmetki, setDataOtmetki] = useState(segodnya());
  const can = ctx.canPlan;

  const load = useCallback(async () => {
    const r = await call<Shema[]>('get', p('/api/projects/{pid}/shemy', { pid: ctx.pid }));
    if (!r.ok) { setMsg(r.error.message); return; }
    setRows(r.data);
    setVybranaShema(v => (v && r.data.some(x => x.id === v) ? v : (r.data[0]?.id ?? null)));
  }, [ctx.pid, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  const shema = rows.find(x => x.id === vybranaShema) || null;
  const shemaId = shema?.id ?? null;
  const fail = useCallback((m: string) => { setMsg(m); ctx.onError?.(m); }, [ctx]);

  // зоны выбранной схемы
  useEffect(() => {
    let zhiv = true;
    setZony([]); setBylo([]); setVybranaZona(null); setRezhim('ruka');
    if (!shemaId) return;
    (async () => {
      const r = await call<ZonyOtvet>('get', p('/api/shemy/{sid}/zony', { sid: shemaId }));
      if (!zhiv) return;
      if (!r.ok) { fail(r.error.message); return; }
      const sp = r.data.zony.map(izZony);
      setZony(sp); setBylo(sp); setRev(r.data.rev);
    })();
    return () => { zhiv = false; };
  }, [shemaId, ctx.reloadToken, fail]);

  const zagruzitGde = useCallback(async () => {
    if (!shemaId) { setGde([]); return; }
    const r = await call<Primenenie[]>('get', p('/api/shemy/{sid}/primenenie', { sid: shemaId }));
    if (r.ok) setGde(r.data);
  }, [shemaId]);
  useEffect(() => { setTid(null); setZadacha(null); setSloj('plan'); void zagruzitGde(); }, [zagruzitGde]);
  // задачу сняли — возвращаемся на слой «План»: закрашивать нечего
  useEffect(() => { if (!tid) setSloj('plan'); }, [tid]);

  // объёмы выбранной задачи
  useEffect(() => {
    let zhiv = true;
    if (!tid) { setZadacha(null); return; }
    void (async () => {
      const r = await call<TaskShema>('get', p('/api/tasks/{tid}/shema', { tid }));
      if (zhiv && r.ok) setZadacha(r.data);
    })();
    return () => { zhiv = false; };
  }, [tid]);

  async function pisatObem(zid: number, znach: string) {
    if (!zadacha || !tid) return;
    const v = znach.trim();
    const r = await call<{ id: number; rev: number }>('put', p('/api/tasks/{tid}/zony', { tid }),
      { rev: zadacha.rev, zony: { [String(zid)]: { obem: v === '' ? null : Number(v.replace(',', '.')) } } });
    if (!r.ok) { fail(r.error.message); return; }
    const svezhee = await call<TaskShema>('get', p('/api/tasks/{tid}/shema', { tid }));
    if (svezhee.ok) setZadacha(svezhee.data);
    ctx.onSaved?.('Объём записан');
  }

  /** перечитать задачу после отметки: процент и статус считает сервер */
  async function obnovitZadachu() {
    if (!tid) return;
    const r = await call<TaskShema>('get', p('/api/tasks/{tid}/shema', { tid }));
    if (r.ok) setZadacha(r.data);
  }

  async function zakrasit(zonaId: number, tochki: [number, number][]) {
    if (!tid || !zadacha) return;
    const r = await call<{ id: number; rev: number; agregat: { procent: number | null } | null }>(
      'post', p('/api/tasks/{tid}/zony/fakt', { tid }),
      { rev: zadacha.rev, dobavit: [{ zona_id: zonaId, geom: { tochki }, data: dataOtmetki || null }] });
    if (!r.ok) { fail(r.error.message); await obnovitZadachu(); return; }
    setMsg(''); ctx.onError?.('');
    await obnovitZadachu();
    ctx.onSaved?.(r.data.agregat ? 'Готовность: ' + (r.data.agregat.procent ?? 0) + '%' : 'Отмечено');
  }

  async function steret(figuraId: number) {
    if (!tid || !zadacha) return;
    const r = await call<{ id: number }>('post', p('/api/tasks/{tid}/zony/fakt', { tid }),
      { rev: zadacha.rev, ubrat: [figuraId] });
    if (!r.ok) { fail(r.error.message); await obnovitZadachu(); return; }
    await obnovitZadachu();
    ctx.onSaved?.('Отметка снята');
  }

  /** дата зоны: своя — уходит в ручные сроки задачи, пустая — возвращает расчёт (#108) */
  async function pisatDatu(zid: number, pole: 'nachalo' | 'okonchanie', znach: string) {
    if (!zadacha || !tid) return;
    const r = await call<{ id: number; rev: number }>('put', p('/api/tasks/{tid}/zony', { tid }),
      { rev: zadacha.rev, zony: { [String(zid)]: { [pole]: znach || null } } });
    if (!r.ok) { fail(r.error.message); await obnovitZadachu(); return; }
    await obnovitZadachu();
    ctx.onSaved?.('Сроки зоны записаны');
  }

  /** снять свои даты у всех зон задачи — сроки снова считает расчёт */
  async function vernutKRaschetu() {
    if (!zadacha || !tid) return;
    const pusto: Record<string, { nachalo: null; okonchanie: null }> = {};
    zadacha.zony.forEach(z => { pusto[String(z.id)] = { nachalo: null, okonchanie: null }; });
    const r = await call<{ id: number; rev: number }>('put', p('/api/tasks/{tid}/zony', { tid }),
      { rev: zadacha.rev, zony: pusto });
    if (!r.ok) { fail(r.error.message); await obnovitZadachu(); return; }
    await obnovitZadachu();
    ctx.onSaved?.('Сроки зон вернулись к расчёту');
  }

  async function snyatPrimenenie(x: Primenenie) {
    if (!shemaId) return;
    const da = await confirmBox('Снять схему с «' + x.rabota + '», корпус ' + x.korpus + '?',
      { ok: 'Снять', opasno: true,
        podpis: 'Затронуто: ' + zadachText(x.zadach)
          + '. Объёмы зон у них будут удалены; сама схема и разметка останутся.' });
    if (!da) return;
    let r = await call<{ snyato: number }>('delete', p('/api/shemy/{sid}/primenenie', { sid: shemaId }),
      { work_id: x.work_id, korpus: x.korpus });
    if (!r.ok && r.error.code === 'est_fakt') {
      // по этим задачам уже закрашено — спрашиваем отдельно, молча факт не теряем
      const vsyo = await confirmBox(r.error.message, { ok: 'Снять вместе с фактом', opasno: true,
        podpis: 'Закрашенное на схеме по этим задачам будет удалено, процент готовности обнулится.' });
      if (!vsyo) return;
      r = await call<{ snyato: number }>('delete', p('/api/shemy/{sid}/primenenie', { sid: shemaId }),
        { work_id: x.work_id, korpus: x.korpus, vmeste_s_faktom: true });
    }
    if (!r.ok) { await alertBox(r.error.message); return; }
    setTid(null);
    void zagruzitGde(); void load();
  }

  const otpechatok = (sp: Risunok[]) => JSON.stringify(sp.map(z => [z.id, z.nazvanie, z.tochki]));
  const gryazno = useMemo(() => otpechatok(zony) !== otpechatok(bylo), [zony, bylo]);
  const vsyaPloshchad = useMemo(() => zony.reduce((s, z) => s + ploshchad(z.tochki), 0), [zony]);

  async function sohranitZony() {
    if (!shema) return;
    setPishem(true);
    const r = await call<{ rev: number; zony: Zona[]; udaleno: number }>(
      'put', p('/api/shemy/{sid}/zony', { sid: shema.id }),
      { rev, zony: zony.map(z => (z.id == null
        ? { nazvanie: z.nazvanie, geom: { tochki: z.tochki } }
        : { id: z.id, nazvanie: z.nazvanie, geom: { tochki: z.tochki } })) });
    setPishem(false);
    if (!r.ok) {
      fail(r.error.message);
      if (r.error.code === 'stale') void load();
      return;
    }
    const sp = r.data.zony.map(izZony);
    setZony(sp); setBylo(sp); setRev(r.data.rev);
    setVybranaZona(null);
    setMsg(''); ctx.onError?.('');
    ctx.onSaved?.('Разметка сохранена');
    void load();     // в списке схем обновится счётчик зон
  }

  function otmenitPravki() {
    setZony(bylo); setVybranaZona(null); setMsg(''); ctx.onError?.('');
  }

  function pereimenovatZonu(z: Risunok) {
    const novoe = window.prompt('Название зоны:', z.nazvanie);
    if (novoe === null) return;
    const t = novoe.trim();
    if (!t) { fail('У зоны должно быть название'); return; }
    if (zony.some(x => x.klyuch !== z.klyuch && x.nazvanie.toLowerCase() === t.toLowerCase())) {
      fail('Зона «' + t + '» уже есть — названия должны различаться');
      return;
    }
    setZony(zz => zz.map(x => (x.klyuch === z.klyuch ? { ...x, nazvanie: t } : x)));
  }

  function peremestit(i: number, kuda: number) {
    setZony(zz => {
      const j = i + kuda;
      if (j < 0 || j >= zz.length) return zz;
      const kop = [...zz];
      [kop[i], kop[j]] = [kop[j], kop[i]];
      return kop;
    });
  }

  function udalitZonu(z: Risunok) {
    setZony(zz => zz.filter(x => x.klyuch !== z.klyuch));
    if (vybranaZona === z.klyuch) setVybranaZona(null);
  }

  async function pereimenovat(x: Shema) {
    const novoe = window.prompt('Название схемы:', x.nazvanie);
    if (novoe === null || novoe.trim() === x.nazvanie) return;
    const r = await call<{ id: number; rev: number }>('patch', p('/api/shemy/{sid}', { sid: x.id }),
      { nazvanie: novoe.trim(), rev: x.rev });
    if (!r.ok) { fail(r.error.message); void load(); return; }
    setMsg(''); ctx.onError?.(''); void load();
  }

  async function udalit(x: Shema) {
    const da = await confirmBox('Удалить схему «' + x.nazvanie + '»?', {
      ok: 'Удалить', opasno: true,
      podpis: 'Чертёж и вся разметка на нём будут удалены с сервера. Восстановить можно только заново.' });
    if (!da) return;
    const r = await call<{ id: number }>('delete', p('/api/shemy/{sid}', { sid: x.id }));
    if (!r.ok) { await alertBox(r.error.message); return; }
    setMsg(''); ctx.onError?.(''); void load();
  }

  /** уход с несохранённой разметкой — спросить */
  async function vybratShemu(id: number) {
    if (id === vybranaShema) return;
    if (gryazno) {
      const da = await confirmBox('Разметка не сохранена — перейти к другой схеме?',
        { ok: 'Перейти', opasno: true, podpis: 'Нарисованное здесь будет потеряно.' });
      if (!da) return;
    }
    setVybranaShema(id);
  }

  return (
    <div className="sh-ekran">
      <div className="sh-levo">
        <div className="sh-spisok">
          <div className="sh-spisok-h">
            <b>Схемы</b>
            {can && <button onClick={() => setNovaya(true)} title="Загрузить чертёж">＋ Схема</button>}
          </div>
          {rows.length === 0 && (
            <div className="sh-pusto">
              Схем пока нет.{can ? ' Загрузите чертёж — на нём размечаются захватки.'
                : ' Их загружает планер.'}
            </div>
          )}
          {rows.map(x => (
            <div key={x.id} className={'sh-strochka' + (x.id === vybranaShema ? ' on' : '')}
              onClick={() => void vybratShemu(x.id)}>
              <div className="sh-imya" title={x.nazvanie}>{x.nazvanie}</div>
              <div className="sh-meta">
                {TIP_IMYA[x.tip] || x.tip}{x.bajt ? ' · ' + ves(x.bajt) : ''}
                {x.zon ? ' · зон: ' + x.zon : ''}
                {x.kogda ? ' · ' + kogdaText(x.kogda) : ''}
              </div>
              {can && (
                <div className="sh-knopki">
                  <a title="Переименовать схему" onClick={e => { e.stopPropagation(); void pereimenovat(x); }}>✎</a>
                  <a title="Удалить схему" onClick={e => { e.stopPropagation(); void udalit(x); }}>✕</a>
                </div>
              )}
            </div>
          ))}
          {msg && <div className="sh-oshibka">{msg}</div>}
        </div>

        {shema && (
          <div className="sh-zony">
            <div className="sh-spisok-h">
              <b>Зоны-захватки</b>
              <span className="sh-meta">{zony.length ? zony.length + ' шт.' : ''}</span>
            </div>
            {can && (
              <>
                <div className="sh-instrumenty sh-sloi">
                  <button className={'sec' + (sloj === 'plan' ? ' on' : '')}
                    onClick={() => { setSloj('plan'); setRezhim('ruka'); }}
                    title="Границы захваток">Слой: План</button>
                  <button className={'sec' + (sloj === 'fakt' ? ' on' : '')} disabled={!zadacha}
                    onClick={() => { setSloj('fakt'); setRezhim('ruka'); }}
                    title={zadacha ? 'Закрашивать сделанное по выбранной задаче'
                      : 'Сначала выберите задачу в списке «Применена к» ниже'}>Слой: Факт</button>
                </div>
                <div className="sh-instrumenty">
                  <button className={'sec' + (rezhim === 'ruka' ? ' on' : '')} onClick={() => setRezhim('ruka')}
                    title={sloj === 'fakt' ? 'Клик по зоне — она сделана целиком'
                      : 'Двигать чертёж, выбирать и править зоны'}>
                    {sloj === 'fakt' ? 'Закрасить зону' : 'Рука'}</button>
                  <button className={'sec' + (rezhim === 'pryamoug' ? ' on' : '')} onClick={() => setRezhim('pryamoug')}
                    title="Тянуть мышью прямоугольник">▭ Прямоугольник</button>
                  <button className={'sec' + (rezhim === 'zona' ? ' on' : '')} onClick={() => setRezhim('zona')}
                    title="По углам: клики по углам, двойной клик — закончить">⬠ По углам</button>
                </div>
                {sloj === 'fakt' && (
                  <div className="sh-pr-ed">
                    Дата отметки:
                    <input type="date" value={dataOtmetki} onChange={e => setDataOtmetki(e.target.value)} />
                  </div>
                )}
              </>
            )}
            {zony.length === 0 && (
              <div className="sh-pusto">
                {can ? 'Обведите участки, которые делают за один заход. Порядок зон — очерёдность работ.'
                  : 'Зоны ещё не размечены.'}
              </div>
            )}
            {zony.map((z, i) => (
              <div key={z.klyuch} className={'sh-zona' + (z.klyuch === vybranaZona ? ' on' : '')}
                onClick={() => setVybranaZona(z.klyuch)}>
                <span className="sh-nomer">{i + 1}</span>
                <span className="sh-zona-imya" title={z.nazvanie}>{z.nazvanie}</span>
                {sloj === 'fakt' && zadacha
                  ? <span className="sh-dolya sh-gotovo" title="Готовность зоны по закрашенной площади">
                      {(zadacha.zony.find(x => x.id === z.id)?.procent ?? 0) + '%'}</span>
                  : <span className="sh-dolya" title="Доля этой зоны в общей размеченной площади">
                      {vsyaPloshchad > 0 ? doliText(ploshchad(z.tochki) / vsyaPloshchad) : ''}</span>}
                {can && (
                  <span className="sh-zona-knopki">
                    <a title="Выше по очереди" onClick={e => { e.stopPropagation(); peremestit(i, -1); }}>↑</a>
                    <a title="Ниже по очереди" onClick={e => { e.stopPropagation(); peremestit(i, 1); }}>↓</a>
                    <a title="Переименовать" onClick={e => { e.stopPropagation(); pereimenovatZonu(z); }}>✎</a>
                    <a title="Удалить зону" onClick={e => { e.stopPropagation(); udalitZonu(z); }}>✕</a>
                  </span>
                )}
              </div>
            ))}
            {can && !gryazno && zony.length > 0 && (
              <div className="sh-sohranit">
                <span className="sh-nesohr" style={{ color: 'var(--plot-muted)', fontWeight: 400 }}>
                  {gde.length ? 'Применена: ' + zadachText(gde.reduce((s2, x) => s2 + x.zadach, 0))
                    : 'Схема ещё ни к чему не применена'}
                </span>
                <button onClick={() => setPrimenyaem(true)}>Применить…</button>
              </div>
            )}
            {can && gryazno && (
              <div className="sh-sohranit">
                <span className="sh-nesohr">Не сохранено</span>
                <button className="sec" onClick={otmenitPravki} disabled={pishem}>Отменить</button>
                <button onClick={() => void sohranitZony()} disabled={pishem}>
                  {pishem ? 'Сохраняю…' : 'Сохранить'}</button>
              </div>
            )}
          </div>
        )}

        {shema && gde.length > 0 && (
          <div className="sh-zony">
            <div className="sh-spisok-h"><b>Применена к</b></div>
            {gde.map(x => (
              <div key={x.work_id + '/' + x.korpus}
                className={'sh-zona' + (zadacha && tid === x.task_id ? ' on' : '')}
                onClick={() => setTid(x.task_id)}>
                <span className="sh-zona-imya" title={x.rabota + ' · ' + x.korpus}>{x.rabota}</span>
                <span className="sh-dolya">{x.korpus} · {x.zadach}</span>
                {can && (
                  <span className="sh-zona-knopki">
                    <a title="Снять схему с этих задач"
                      onClick={e => { e.stopPropagation(); void snyatPrimenenie(x); }}>✕</a>
                  </span>
                )}
              </div>
            ))}
            {zadacha && zadacha.shema && (
              <div className="sh-obemy">
                <div className="sh-meta">
                  Объёмы задачи: {zadacha.rabota} · {zadacha.korpus} · этаж {zadacha.etazh}
                </div>
                {zadacha.zony.map((z, i) => (
                  <div key={z.id} className="sh-zona-stroka">
                    <div className="sh-pr-obem">
                      <span>{i + 1}. {z.nazvanie}</span>
                      <input defaultValue={z.obem == null ? '' : String(z.obem)} disabled={!can}
                        title={'Объём. Единица: ' + (z.ed_izm || 'не указана')}
                        onBlur={e => { if (e.target.value !== (z.obem == null ? '' : String(z.obem)))
                          void pisatObem(z.id, e.target.value); }} />
                    </div>
                    {/* запись по уходу из поля, а не на каждое нажатие: иначе каждая
                        цифра уходила бы на сервер и вторая правка получала бы 409
                        (версия строки уже другая) — поймано на тесте 2026-09-19.
                        key перевешивает поле, когда сервер вернул другое значение */}
                    <div className={'sh-daty' + (z.iz_rascheta ? ' raschet' : '')}>
                      <input type="date" key={'n' + z.id + (z.nachalo || '')}
                        defaultValue={z.nachalo || ''} disabled={!can}
                        title={z.iz_rascheta ? 'Из расчёта — поставьте свою дату, и сроки работы подстроятся'
                          : 'Своя дата планера'}
                        onBlur={e => { if (e.target.value !== (z.nachalo || ''))
                          void pisatDatu(z.id, 'nachalo', e.target.value); }} />
                      <span>—</span>
                      <input type="date" key={'o' + z.id + (z.okonchanie || '')}
                        defaultValue={z.okonchanie || ''} disabled={!can}
                        title={z.iz_rascheta ? 'Из расчёта' : 'Своя дата планера'}
                        onBlur={e => { if (e.target.value !== (z.okonchanie || ''))
                          void pisatDatu(z.id, 'okonchanie', e.target.value); }} />
                    </div>
                  </div>
                ))}
                <div className="sh-meta">
                  Серые даты взяты из расчёта и нигде не хранятся. Поставили свою — начало
                  и окончание работы на этаже складываются из дат зон.
                </div>
                {can && zadacha.zony.some(z => !z.iz_rascheta) && (
                  <button className="sec" onClick={() => void vernutKRaschetu()}>Вернуть к расчёту</button>
                )}
                <div className="sh-meta">Объёмы у каждой задачи свои; у остальных этажей — те, что задали при применении.</div>
                {zadacha.zony.length > 0 && (
                  <div className="sh-itog">
                    Готовность по площади: <b>{Math.round(
                      100 * zadacha.zony.reduce((s2, z) => s2 + z.sdelano, 0)
                      / Math.max(1, zadacha.zony.reduce((s2, z) => s2 + z.ploshchad, 0)))}%</b>
                    {zadacha.fakt.length ? ' · отметок: ' + zadacha.fakt.length : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sh-pravo">
        {shema
          ? <Prosmotr key={shema.id} shema={shema} zony={zony} vybrana={vybranaZona}
              rezhim={can ? rezhim : 'ruka'} mozhno={can} onOshibka={fail}
              sloj={zadacha ? sloj : 'plan'} fakt={(zadacha && zadacha.fakt) || []}
              onZony={f => setZony(f)} onVybrat={setVybranaZona}
              onFakt={(zid, tt) => void zakrasit(zid, tt)} onSteret={id => void steret(id)} />
          : <div className="sh-pusto-bolshoe">Выберите схему слева или загрузите чертёж</div>}
      </div>

      {primenyaem && shema && (
        <PrimenitOkno pid={ctx.pid} sid={shema.id} zony={zony} onOshibka={fail}
          onOtmena={() => setPrimenyaem(false)}
          onGotovo={n => {
            setPrimenyaem(false); setMsg(''); ctx.onError?.('');
            ctx.onSaved?.('Схема применена: ' + zadachText(n));
            void zagruzitGde(); void load();
          }} />
      )}
      {novaya && (
        <NovayaShema pid={ctx.pid} onOshibka={fail}
          onOtmena={() => setNovaya(false)}
          onGotovo={() => { setNovaya(false); setMsg(''); ctx.onError?.(''); void load(); }} />
      )}
    </div>
  );
}
